/**
 * Prefacturacion.jsx · v101.193.0 / v101.195.0 · Prefacturación de contratistas (Procedimiento MC-GFS-PR-01).
 * TOTAL = Actividades realizadas − Correcciones documentales ± Cambios de tipo de solución (sin garantías ácidas).
 * Actividades: OTs cerradas en AlfaField en el corte (1–15 / 16–fin de mes). Control de Calidad registra por OT la
 * corrección y el cambio de tipo; la prefactura pasa por borrador → revisada → aprobada (copia congelada).
 * v101.195.0 · El contratista de cada OT es el técnico de AlfaField que la ejecutó (rol contratista o asignación en
 * «Técnicos y contratista»), no solo el contratista de Odoo. Las OTs de personal interno se listan aparte.
 * v101.206.0 · Filtros por contratista y rango de fechas (dentro del período); el filtro reemplaza «← Todos los
 *   contratistas»; botón «Informe» (PDF de la autofiscalización) al lado de cada OT.
 * Datos: /api/v1/prefacturacion/*. Descargas PDF (por contratista y consolidado) y Excel consolidado con marca AlfaField.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Download, FileText, Loader2, ChevronDown, Search, AlertTriangle, CheckCircle2, RotateCcw, X, Pencil,
  Save, Trash2, ClipboardCheck } from "lucide-react";

const C = { bg: "#E4EAF0", surf: "#ECF0F5", ink: "#0A3747", text: "#1E293B", sub: "#475569", mute: "#64748B",
  lime: "#E0E621", serie: "#0F7FA8", desc: "#E0604D", ok: "#16A34A", warn: "#D97706" };
const OUT = "-6px -6px 14px rgba(255,255,255,.95), 6px 6px 14px rgba(163,177,198,.55)";
const OUT_LG = "-10px -10px 22px rgba(255,255,255,.95), 10px 10px 22px rgba(163,177,198,.55)";
const OUT_SM = "-3px -3px 8px rgba(255,255,255,.95), 3px 3px 8px rgba(163,177,198,.55)";
const IN = "inset -3px -3px 7px rgba(255,255,255,.95), inset 3px 3px 7px rgba(163,177,198,.55)";
const IN_SM = "inset -2px -2px 5px rgba(255,255,255,.95), inset 2px 2px 5px rgba(163,177,198,.55)";
const MONO = "'JetBrains Mono', ui-monospace, monospace";
const card = { padding: 22, borderRadius: 24, background: C.surf, boxShadow: OUT_LG, display: "flex", flexDirection: "column", gap: 14, minWidth: 0 };
const well = { borderRadius: 14, background: C.bg, boxShadow: IN_SM };
const h2 = { fontSize: 14, fontWeight: 700, color: C.ink, margin: 0 };
const cap = { fontSize: 12, color: C.sub, margin: 0 };
const th = { textAlign: "left", fontSize: 11, fontWeight: 700, color: "#FFF", background: C.ink, padding: "9px 10px", whiteSpace: "nowrap" };
const td = { fontSize: 12.5, color: C.text, padding: "8px 10px", borderBottom: "1px solid #D9E1EA", verticalAlign: "top" };
const inp = { border: 0, padding: "9px 11px", borderRadius: 11, background: C.bg, boxShadow: IN_SM, fontFamily: "Inter", fontSize: 13, color: C.text, width: "100%" };

const money = (v, signo = false) => {
  const n = Number(v || 0);
  const s = Math.abs(n).toLocaleString("es-EC", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${n < 0 ? "-" : signo && n > 0 ? "+" : ""}$ ${s}`;
};
const pct = (v) => (v == null ? "—" : `${Number(v).toFixed(1).replace(".", ",")} %`);
// v101.206.0 · PDF de la autofiscalización (informe técnico) de una OT, en otra pestaña
async function verInforme(taskId) {
  const win = window.open("", "_blank");
  try {
    const { data } = await api.get(`/v1/ot_smart/${taskId}/informe-pdf`);
    if (!data?.exists) throw new Error("La OT no tiene informe de fiscalización.");
    const r = await api.get(`/v1/fiscalizacion/submissions/${data.submission_id}/pdf-link`);
    if (!r.data?.url) throw new Error("sin enlace");
    if (win) win.location.href = r.data.url; else window.open(r.data.url, "_blank");
  } catch (e) {
    if (win) win.close();
    toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : (e?.message || "No se pudo abrir el informe"));
  }
}
const errMsg = (e, d) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : d);
const ESTADO = {
  borrador: { label: "Borrador", fg: "#475569", bg: "#E2E8F0" },
  revisada: { label: "Revisada", fg: "#075985", bg: "#E0F2FE" },
  aprobada: { label: "Aprobada", fg: "#15803D", bg: "#DCFCE7" },
};

function Pill({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{ border: 0, cursor: "pointer", fontFamily: "Inter", fontSize: 13, padding: "10px 16px", borderRadius: 13,
      fontWeight: active ? 700 : 600, color: active ? C.ink : C.sub, background: active ? C.surf : "transparent", boxShadow: active ? OUT_SM : "none" }}>
      {children}
    </button>
  );
}

function Btn({ onClick, children, primary, disabled, title, danger }) {
  return (
    <button onClick={onClick} disabled={disabled} title={title} style={{ display: "inline-flex", alignItems: "center", gap: 8, border: 0,
      font: `${primary ? 700 : 600} 13px Inter`, color: primary ? "#FFF" : danger ? C.desc : C.ink, background: primary ? C.ink : C.surf,
      padding: "10px 15px", borderRadius: 13, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
      boxShadow: primary ? "-4px -4px 10px rgba(255,255,255,.9), 4px 4px 12px rgba(10,55,71,.35)" : OUT_SM }}>
      {children}
    </button>
  );
}

function Estado({ e }) {
  const s = ESTADO[e] || ESTADO.borrador;
  return <span style={{ fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 999, color: s.fg, background: s.bg, whiteSpace: "nowrap" }}>{s.label}</span>;
}

function Tiles({ items }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
      {items.map(([v, l, hi]) => (
        <div key={l} style={{ padding: "16px 18px", borderRadius: 18, background: hi ? C.ink : C.surf, boxShadow: OUT }}>
          <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: "-.02em", color: hi ? C.lime : C.ink }}>{v}</div>
          <div style={{ fontSize: 12, color: hi ? "#C9D9DF" : C.sub, marginTop: 4 }}>{l}</div>
        </div>
      ))}
    </div>
  );
}

/* Barras horizontales de una serie, ordenadas, con etiqueta directa y detalle al pasar el mouse */
function Bars({ rows, onPick }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div role="list" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {rows.map((r) => (
        <button key={r.label} role="listitem" onClick={() => onPick && onPick(r.label)} title={r.tip}
          style={{ display: "grid", gridTemplateColumns: "minmax(140px, 260px) 1fr", alignItems: "center", gap: 10, border: 0, background: "transparent", padding: "3px 0", cursor: onPick ? "pointer" : "default", textAlign: "left" }}
          className="pf-bar">
          <span style={{ fontSize: 12, color: C.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textAlign: "right" }}>{r.label}</span>
          <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <span style={{ height: 14, width: `${Math.max(1.5, (r.value / max) * 78)}%`, background: C.serie, borderRadius: "0 4px 4px 0", transition: "width .6s" }} />
            <span style={{ fontSize: 12, fontWeight: 700, color: C.text, whiteSpace: "nowrap" }}>{r.text}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

function ValTable({ title, rows, signo }) {
  const n = rows.reduce((a, r) => a + r.cantidad, 0);
  const t = rows.reduce((a, r) => a + r.subtotal, 0);
  return (
    <div style={{ overflowX: "auto", borderRadius: 14 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", background: C.surf }}>
        <thead><tr><th style={th}>{title}</th><th style={{ ...th, textAlign: "right" }}>Cantidad</th><th style={{ ...th, textAlign: "right" }}>Valor unitario</th><th style={{ ...th, textAlign: "right" }}>Subtotal</th></tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td style={{ ...td, color: C.mute }} colSpan={4}>Sin registros</td></tr>}
          {rows.map((r) => (
            <tr key={r.concepto} style={{ color: r.cantidad ? C.text : "#94A3B8" }}>
              <td style={{ ...td, color: "inherit" }}>{r.concepto}</td>
              <td style={{ ...td, color: "inherit", textAlign: "right" }}>{r.cantidad}</td>
              <td style={{ ...td, color: "inherit", textAlign: "right" }}>{money(r.valor, signo)}</td>
              <td style={{ ...td, color: "inherit", textAlign: "right" }}>{r.cantidad ? money(r.subtotal) : "—"}</td>
            </tr>
          ))}
          <tr><td style={{ ...td, fontWeight: 700 }}>TOTAL</td><td style={{ ...td, fontWeight: 700, textAlign: "right" }}>{n}</td><td style={td} />
            <td style={{ ...td, fontWeight: 800, textAlign: "right", color: t < 0 ? C.desc : C.ink }}>{money(t)}</td></tr>
        </tbody>
      </table>
    </div>
  );
}

/* ── Modal: corrección documental y cambio de tipo de una OT ── */
function OtModal({ a, cat, onClose, onSaved, locked }) {
  const [tipo, setTipo] = useState(a.correccion || "");
  const [motivo, setMotivo] = useState(a.motivo_correccion || "");
  const [nota, setNota] = useState("");
  const [cnueva, setCnueva] = useState(a.categoria_modificada || "");
  const [mcambio, setMcambio] = useState(a.motivo_cambio || "");
  const [busy, setBusy] = useState(false);
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); onSaved(); } catch (e) { toast.error(errMsg(e, "No se pudo guardar")); } finally { setBusy(false); }
  };
  const tarifa = Object.fromEntries((cat?.categorias || []).map((c) => [c.nombre, c.valor]));
  const dif = cnueva && a.categoria && tarifa[cnueva] != null && tarifa[a.categoria] != null ? tarifa[cnueva] - tarifa[a.categoria] : null;
  return (
    <div role="dialog" aria-modal="true" style={{ position: "fixed", inset: 0, background: "rgba(11,31,44,.45)", zIndex: 60, display: "grid", placeItems: "center", padding: 16 }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...card, width: "min(640px, 100%)", maxHeight: "90vh", overflowY: "auto", background: C.surf }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
          <div>
            <div style={{ fontFamily: MONO, fontSize: 11, color: C.sub }}>OT {a.ot} · contrato {a.contrato || "—"} · {a.fecha}</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.ink, marginTop: 4 }}>{a.solucion}</div>
            <div style={cap}>Categoría de pago: <b>{a.categoria || "SIN CLASIFICAR"}</b> · Técnico: {a.tecnico || "—"}</div>
          </div>
          <button onClick={onClose} aria-label="Cerrar" style={{ border: 0, background: "transparent", cursor: "pointer" }}><X size={18} color={C.sub} /></button>
        </div>
        {locked && <div style={{ ...well, padding: 10, fontSize: 12, color: C.warn }}>La prefactura está aprobada: no se puede modificar. Un administrador puede reabrirla.</div>}

        <section style={{ ...well, padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={h2}>Corrección documental</div>
          <p style={cap}>Informe no corregido, corregido dentro de las 48 h, o justificado (sin descuento).</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 8 }}>
            {(cat?.correccion_tipos || []).map((t) => (
              <button key={t.nombre} onClick={() => setTipo(t.nombre)} disabled={locked} style={{ border: 0, cursor: "pointer", padding: "10px", borderRadius: 12, textAlign: "left",
                background: tipo === t.nombre ? C.surf : "transparent", boxShadow: tipo === t.nombre ? OUT_SM : "none", fontFamily: "Inter" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.ink }}>{t.nombre}</div>
                <div style={{ fontSize: 11, color: C.sub }}>Descuento {money(t.valor)}</div>
              </button>
            ))}
          </div>
          <input list="pf-nc" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo (no conformidad) — elija o escriba" style={inp} disabled={locked} />
          <datalist id="pf-nc">{(cat?.no_conformidades || []).map((n) => <option key={n} value={n} />)}</datalist>
          <input value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Nota interna (opcional)" style={inp} disabled={locked} />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
            {a.correccion && <Btn danger disabled={busy || locked} onClick={() => run(() => api.delete(`/v1/prefacturacion/correccion/${a.id}`), "Corrección quitada")}><Trash2 size={14} /> Quitar</Btn>}
            <Btn primary disabled={busy || locked || !tipo} onClick={() => run(() => api.put(`/v1/prefacturacion/correccion/${a.id}`, { tipo, motivo, nota }), "Corrección guardada")}><Save size={14} /> Guardar corrección</Btn>
          </div>
        </section>

        <section style={{ ...well, padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={h2}>Cambio de tipo de solución</div>
          <p style={cap}>Categoría que corresponde a lo realmente ejecutado. Se ajusta la diferencia de tarifas.</p>
          <select value={cnueva} onChange={(e) => setCnueva(e.target.value)} style={inp} disabled={locked}>
            <option value="">Sin cambio</option>
            {(cat?.categorias || []).filter((c) => c.activo !== false && c.nombre !== a.categoria).map((c) => <option key={c.nombre} value={c.nombre}>{c.nombre} · {money(c.valor)}</option>)}
          </select>
          <input list="pf-mc" value={mcambio} onChange={(e) => setMcambio(e.target.value)} placeholder="Motivo del cambio — elija o escriba" style={inp} disabled={locked} />
          <datalist id="pf-mc">{(cat?.motivos_cambio || []).map((n) => <option key={n} value={n} />)}</datalist>
          {dif != null && <div style={{ fontSize: 12, color: dif < 0 ? C.desc : C.ink, fontWeight: 700 }}>Ajuste: {money(dif, true)} ({a.categoria} › {cnueva})</div>}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
            {a.categoria_modificada && <Btn danger disabled={busy || locked} onClick={() => run(() => api.delete(`/v1/prefacturacion/cambio/${a.id}`), "Cambio quitado")}><Trash2 size={14} /> Quitar</Btn>}
            <Btn primary disabled={busy || locked || !cnueva} onClick={() => run(() => api.put(`/v1/prefacturacion/cambio/${a.id}`, { categoria_nueva: cnueva, motivo: mcambio }), "Cambio guardado")}><Save size={14} /> Guardar cambio</Btn>
          </div>
        </section>
      </div>
    </div>
  );
}

/* ── Vista de un contratista ── */
function Contratista({ per, nombre, cat, role, rango, onChanged }) {
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [flt, setFlt] = useState("todas");
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [parcial, setParcial] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/v1/prefacturacion/contratista", { params: { anio: per.anio, numero: per.numero, nombre, ...rango } });
      setB(data.bloque); setParcial(!!data.periodo?.parcial);
    } catch (e) { setB(null); toast.error(errMsg(e, "No se pudo cargar el contratista")); } finally { setLoading(false); }
  }, [per, nombre, rango]);
  useEffect(() => { load(); }, [load]);
  const canEdit = ["super_admin", "admin", "fiscalizer"].includes(role);
  const canApprove = ["super_admin", "admin"].includes(role);
  const locked = b?.estado === "aprobada";
  const accion = async (acc) => {
    setBusy(true);
    try {
      await api.post("/v1/prefacturacion/estado", { anio: per.anio, numero: per.numero, contratista: nombre, accion: acc });
      toast.success({ revisar: "Prefactura revisada", aprobar: "Prefactura aprobada", reabrir: "Prefactura reabierta" }[acc]);
      await load(); onChanged();
    } catch (e) { toast.error(errMsg(e, "No se pudo cambiar el estado")); } finally { setBusy(false); }
  };
  const pdf = async () => {
    try {
      const r = await api.get("/v1/prefacturacion/pdf", { params: { anio: per.anio, numero: per.numero, contratista: nombre, ...rango }, responseType: "blob", timeout: 300000 });
      const u = URL.createObjectURL(r.data); const el = document.createElement("a");
      el.href = u; el.download = `Prefactura_P${per.numero}_${per.anio}_${nombre.replace(/ /g, "_")}.pdf`; el.click(); URL.revokeObjectURL(u);
    } catch { toast.error("No se pudo generar el PDF"); }
  };
  const rows = useMemo(() => {
    const s = q.trim().toUpperCase();
    return (b?.anexos || []).filter((a) => {
      if (flt === "correccion" && !a.correccion) return false;
      if (flt === "cambio" && !a.categoria_modificada) return false;
      if (flt === "nofact" && a.categoria && a.categoria !== "N/A") return false;
      if (flt === "sinclas" && a.categoria) return false;
      return !s || `${a.ot} ${a.contrato} ${a.solucion} ${a.tecnico}`.toUpperCase().includes(s);
    });
  }, [b, q, flt]);
  if (!b) return <section style={card}>{loading ? <span style={cap}><Loader2 size={14} className="animate-spin" /> Cargando…</span> : <span style={cap}>Sin datos.</span>}</section>;
  const t = b.totales, k = b.kpi;
  return (
    <>
      <section style={{ ...card, flexDirection: "row", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 22, fontWeight: 800, color: C.ink }}>{b.contratista}</span><Estado e={b.estado} />
          </div>
          <span style={cap}>Sucursal {b.sucursal || "—"} · Centro de costo {b.centro_costo || "—"}{b.aprobada_por ? ` · Aprobada por ${b.aprobada_por}` : ""}{b.revisada_por && !b.aprobada_por ? ` · Revisada por ${b.revisada_por}` : ""}</span>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <Btn onClick={pdf}><FileText size={15} /> PDF</Btn>
          {canEdit && !parcial && b.estado === "borrador" && <Btn disabled={busy} onClick={() => accion("revisar")}><ClipboardCheck size={15} /> Marcar revisada</Btn>}
          {canApprove && !parcial && b.estado !== "aprobada" && <Btn primary disabled={busy || k.sin_clasificar > 0} title={k.sin_clasificar ? "Hay OTs sin categoría de pago" : ""} onClick={() => accion("aprobar")}><CheckCircle2 size={15} color={C.lime} /> Aprobar</Btn>}
          {canApprove && !parcial && b.estado !== "borrador" && <Btn disabled={busy} onClick={() => accion("reabrir")}><RotateCcw size={15} /> Reabrir</Btn>}
        </div>
      </section>
      {parcial && <div style={{ ...card, flexDirection: "row", alignItems: "center", color: C.serie }}><AlertTriangle size={18} /> Rango de fechas parcial del período: solo consulta. Para revisar o aprobar la prefactura quite el filtro de fechas.</div>}
      <Tiles items={[[k.actividades, "Actividades facturables"], [money(t.actividades), "Valor de actividades"],
        [money(t.correcciones + t.cambios, true), `Ajustes (${k.observadas} correcciones · ${k.cambios} cambios)`],
        [money(t.total), "Total a facturar", true], [pct(k.pct_sin_obs), "Informes sin observaciones"]]} />
      {k.sin_clasificar > 0 && <div style={{ ...card, flexDirection: "row", alignItems: "center", color: C.warn }}><AlertTriangle size={18} /> {k.sin_clasificar} OT con tipo de solución sin categoría de pago. Asígnela en «Tarifas y clasificación».</div>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: 18 }}>
        <section style={card}><ValTable title="ACTIVIDADES REALIZADAS" rows={b.actividades} /></section>
        <section style={card}>
          <ValTable title="CORRECCIONES DOCUMENTALES" rows={b.correcciones} />
          <ValTable title="CAMBIOS DE TIPO DE SOLUCIÓN" rows={b.cambios} signo />
        </section>
      </div>
      <section style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div><h2 style={h2}>Actividades del período ({b.anexos.length})</h2><p style={cap}>Pulse una OT para registrar la corrección documental o el cambio de tipo de solución.</p></div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <div role="group" style={{ display: "flex", gap: 4, padding: 4, borderRadius: 13, background: C.bg, boxShadow: IN }}>
              {[["todas", "Todas"], ["correccion", "Con corrección"], ["cambio", "Con cambio"], ["nofact", "No facturables"], ["sinclas", "Sin clasificar"]].map(([v, l]) => <Pill key={v} active={flt === v} onClick={() => setFlt(v)}>{l}</Pill>)}
            </div>
            <span style={{ position: "relative", minWidth: 220 }}>
              <Search size={14} color={C.mute} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)" }} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="OT, contrato, técnico…" style={{ ...inp, paddingLeft: 30 }} />
            </span>
          </div>
        </div>
        <div style={{ overflowX: "auto", borderRadius: 14 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
            <thead><tr>{["Fecha", "OT", "Informe", "Contrato", "Tipo de solución / categoría", "Técnico", "Corrección", "Cambio de tipo", ""].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id} onClick={() => canEdit && setSel(a)} style={{ cursor: canEdit ? "pointer" : "default", color: !a.categoria || a.categoria === "N/A" ? "#94A3B8" : C.text }}>
                  <td style={{ ...td, color: "inherit", whiteSpace: "nowrap", fontFamily: MONO, fontSize: 11.5 }}>{a.fecha}</td>
                  <td style={{ ...td, color: "inherit", fontFamily: MONO, fontSize: 11.5 }}>{a.ot}</td>
                  <td style={td}>
                    <button onClick={(e) => { e.stopPropagation(); verInforme(a.id); }} title="Ver el PDF de la fiscalización (informe técnico)"
                      data-testid={`pf-informe-${a.id}`}
                      style={{ border: 0, borderRadius: 9, padding: "5px 8px", background: C.bg, boxShadow: OUT_SM, cursor: "pointer", fontSize: 11, fontWeight: 600, color: C.ink, display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
                      <FileText size={12} /> PDF
                    </button>
                  </td>
                  <td style={{ ...td, color: "inherit" }}>{a.contrato}</td>
                  <td style={{ ...td, color: "inherit" }}>{a.solucion}<div style={{ fontSize: 11, color: a.categoria ? C.mute : C.warn }}>{a.categoria || "SIN CLASIFICAR"}</div></td>
                  <td style={{ ...td, color: "inherit" }}>{a.tecnico}</td>
                  <td style={{ ...td, background: a.correccion ? "#FDECEA" : "transparent" }}>{a.correccion && <><b style={{ fontSize: 11.5 }}>{a.correccion}</b><div style={{ fontSize: 11, color: C.sub }}>{a.motivo_correccion}</div></>}</td>
                  <td style={{ ...td, background: a.categoria_modificada ? "#E6F3F8" : "transparent" }}>{a.categoria_modificada && <><b style={{ fontSize: 11.5 }}>› {a.categoria_modificada}</b><div style={{ fontSize: 11, color: C.sub }}>{a.motivo_cambio}</div></>}</td>
                  <td style={td}>{canEdit && <Pencil size={14} color={C.sub} />}</td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td style={{ ...td, color: C.mute }} colSpan={9}>Sin actividades con ese filtro.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
      {sel && <OtModal a={sel} cat={cat} locked={locked} onClose={() => setSel(null)} onSaved={async () => { setSel(null); await load(); onChanged(); }} />}
    </>
  );
}

const ROL = { contractor: "Contratista", contratista: "Contratista", technician: "Técnico interno", tecnico: "Técnico interno", soporte_tecnico: "Soporte técnico" };

/* ── v101.195.0 · Técnico de AlfaField → contratista que factura sus OTs ── */
function TecnicosContratista({ canEdit, onChanged }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const load = useCallback(() => api.get("/v1/prefacturacion/catalogos/usuarios").then(({ data }) => setData(data)).catch(() => setData({ items: [], contratistas: [] })), []);
  useEffect(() => { load(); }, [load]);
  const save = async (uid, val) => {
    try {
      await api.put("/v1/prefacturacion/catalogos/usuario", { user_id: uid, contratista: val === "__auto" ? null : val === "__interno" ? "" : val });
      toast.success("Técnico actualizado"); load(); onChanged();
    } catch (e) { toast.error(errMsg(e, "No se pudo guardar")); }
  };
  const items = (data?.items || []).filter((x) => !q.trim() || `${x.name} ${x.email} ${x.contratista}`.toUpperCase().includes(q.trim().toUpperCase()));
  const FUENTE = { tecnico: "automático · el técnico es contratista", asignado: "asignado a mano", interno: "personal interno · no se factura", odoo: "contratista de Odoo" };
  return (
    <section style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <div><h2 style={h2}>Técnicos y contratista ({(data?.items || []).length})</h2>
          <p style={cap}>Cada OT se factura al contratista del técnico que la cerró en AlfaField. Un usuario con rol contratista factura a su nombre; un ayudante se asigna a su contratista; el personal interno no se factura.</p></div>
        <span style={{ position: "relative", minWidth: 260 }}>
          <Search size={14} color={C.mute} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)" }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar técnico…" style={{ ...inp, paddingLeft: 30 }} />
        </span>
      </div>
      {!data ? <Loader2 size={18} className="animate-spin" /> : (
        <div style={{ overflowX: "auto", maxHeight: 460 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>{["Técnico", "Rol", "OT 45 días", "Factura a", "Origen"].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>{items.map((x) => (
              <tr key={x.uid}>
                <td style={td}>{x.name}<div style={{ fontSize: 11, color: C.mute }}>{x.activo ? "" : "inactivo"}</div></td>
                <td style={td}>{ROL[x.rol] || x.rol}</td>
                <td style={{ ...td, textAlign: "right", fontFamily: MONO }}>{x.ots_45d}</td>
                <td style={{ ...td, minWidth: 260 }}>
                  <select disabled={!canEdit} value={x.asignado ? (x.contratista || "__interno") : "__auto"} onChange={(e) => save(x.uid, e.target.value)} style={inp}>
                    <option value="__auto">Automático{x.asignado ? "" : ` · ${x.contratista || "interno"}`}</option>
                    <option value="__interno">Personal interno (no se factura)</option>
                    {(data.contratistas || []).map((n) => <option key={n} value={n}>{n}</option>)}
                    {x.contratista && !(data.contratistas || []).includes(x.contratista) && <option value={x.contratista}>{x.contratista}</option>}
                  </select>
                </td>
                <td style={{ ...td, fontSize: 11.5, color: C.sub }}>{FUENTE[x.fuente] || x.fuente}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/* ── Tarifas, clasificación, contratistas y firmas ── */
function Catalogos({ cat, sinClasificar, role, onChanged }) {
  const isAdmin = ["super_admin", "admin"].includes(role);
  const canEdit = isAdmin || role === "fiscalizer";
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState({});
  const [firmas, setFirmas] = useState([]);
  useEffect(() => { setFirmas((cat?.firmas || []).map(([nombre, cargo]) => ({ nombre, cargo }))); }, [cat]);
  const save = async (url, body, ok) => {
    try { await api.put(url, body); toast.success(ok); onChanged(); } catch (e) { toast.error(errMsg(e, "No se pudo guardar")); }
  };
  const catNames = (cat?.categorias || []).map((c) => c.nombre);
  const mapeo = (cat?.mapeo || []).filter((m) => !q.trim() || m.nombre.toUpperCase().includes(q.trim().toUpperCase()));
  return (
    <>
      {sinClasificar.length > 0 && (
        <section style={{ ...card, border: `1px solid ${C.warn}` }}>
          <h2 style={{ ...h2, color: C.warn, display: "flex", alignItems: "center", gap: 8 }}><AlertTriangle size={16} /> Tipos de solución sin categoría de pago</h2>
          <p style={cap}>Asigne la categoría (o N/A si no se paga). Se guarda para los próximos períodos.</p>
          {sinClasificar.map((s) => (
            <div key={s.nombre} style={{ ...well, padding: 10, display: "grid", gridTemplateColumns: "1fr 260px", gap: 10, alignItems: "center" }}>
              <span style={{ fontSize: 12.5 }}>{s.nombre} <span style={{ color: C.mute }}>· {s.ots} OT</span></span>
              <select disabled={!canEdit} defaultValue="" onChange={(e) => e.target.value && save("/v1/prefacturacion/catalogos/mapeo", { nombre: s.nombre, categoria: e.target.value }, "Clasificado")} style={inp}>
                <option value="">Elegir categoría…</option>{catNames.map((n) => <option key={n} value={n}>{n}</option>)}<option value="N/A">N/A (no se factura)</option>
              </select>
            </div>
          ))}
        </section>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: 18 }}>
        <section style={card}>
          <h2 style={h2}>Tarifas por categoría</h2><p style={cap}>Valor por actividad. Solo un administrador puede cambiarlas; las prefacturas aprobadas conservan el valor con que se aprobaron.</p>
          {(cat?.categorias || []).map((c) => (
            <div key={c.nombre} style={{ display: "grid", gridTemplateColumns: "1fr 120px 90px", gap: 8, alignItems: "center" }}>
              <span style={{ fontSize: 12.5 }}>{c.nombre}</span>
              <input type="number" step="0.01" min="0" disabled={!isAdmin} defaultValue={c.valor} onChange={(e) => setEdit({ ...edit, [c.nombre]: e.target.value })} style={inp} />
              <Btn disabled={!isAdmin || edit[c.nombre] == null} onClick={() => save("/v1/prefacturacion/catalogos/categoria", { nombre: c.nombre, valor: edit[c.nombre], activo: true }, "Tarifa guardada")}><Save size={14} /></Btn>
            </div>
          ))}
          <h2 style={{ ...h2, marginTop: 8 }}>Descuento por corrección</h2>
          {(cat?.correccion_tipos || []).map((c) => (
            <div key={c.nombre} style={{ display: "grid", gridTemplateColumns: "1fr 120px 90px", gap: 8, alignItems: "center" }}>
              <span style={{ fontSize: 12.5 }}>{c.nombre}</span>
              <input type="number" step="0.01" min="0" disabled={!isAdmin} defaultValue={c.valor} onChange={(e) => setEdit({ ...edit, ["c:" + c.nombre]: e.target.value })} style={inp} />
              <Btn disabled={!isAdmin || edit["c:" + c.nombre] == null} onClick={() => save("/v1/prefacturacion/catalogos/correccion-tipo", { nombre: c.nombre, valor: edit["c:" + c.nombre] }, "Descuento guardado")}><Save size={14} /></Btn>
            </div>
          ))}
        </section>
        <section style={card}>
          <h2 style={h2}>Contratistas</h2><p style={cap}>Centro de costo, sucursal y RUC que salen en la prefactura.</p>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>{["Contratista", "Sucursal", "C. costo", "RUC", ""].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {(cat?.contratistas || []).map((c) => {
                  const e = edit["k:" + c.nombre] || c;
                  const set = (f, v) => setEdit({ ...edit, ["k:" + c.nombre]: { ...e, [f]: v } });
                  return (
                    <tr key={c.nombre}>
                      <td style={td}>{c.nombre}</td>
                      <td style={td}><input disabled={!canEdit} value={e.sucursal || ""} onChange={(x) => set("sucursal", x.target.value)} style={{ ...inp, minWidth: 110 }} /></td>
                      <td style={td}><input disabled={!canEdit} value={e.centro_costo || ""} onChange={(x) => set("centro_costo", x.target.value)} style={{ ...inp, width: 70 }} /></td>
                      <td style={td}><input disabled={!canEdit} value={e.ruc || ""} onChange={(x) => set("ruc", x.target.value)} style={{ ...inp, minWidth: 120 }} /></td>
                      <td style={td}><Btn disabled={!canEdit || !edit["k:" + c.nombre]} onClick={() => save("/v1/prefacturacion/catalogos/contratista", e, "Contratista guardado")}><Save size={14} /></Btn></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <h2 style={{ ...h2, marginTop: 8 }}>Firmas de la prefactura</h2>
          {firmas.map((f, i) => (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <input disabled={!isAdmin} value={f.nombre} onChange={(e) => setFirmas(firmas.map((x, j) => (j === i ? { ...x, nombre: e.target.value } : x)))} style={inp} />
              <input disabled={!isAdmin} value={f.cargo} onChange={(e) => setFirmas(firmas.map((x, j) => (j === i ? { ...x, cargo: e.target.value } : x)))} style={inp} />
            </div>
          ))}
          {isAdmin && <div><Btn onClick={() => save("/v1/prefacturacion/catalogos/firmas", { firmas }, "Firmas guardadas")}><Save size={14} /> Guardar firmas</Btn></div>}
        </section>
      </div>
      <TecnicosContratista canEdit={canEdit} onChanged={onChanged} />
      <section style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <div><h2 style={h2}>Clasificación de tipos de solución ({(cat?.mapeo || []).length})</h2><p style={cap}>Qué categoría de pago corresponde a cada tipo de solución de Odoo.</p></div>
          <span style={{ position: "relative", minWidth: 260 }}>
            <Search size={14} color={C.mute} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)" }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar tipo de solución…" style={{ ...inp, paddingLeft: 30 }} />
          </span>
        </div>
        <div style={{ maxHeight: 460, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
          {mapeo.map((m) => (
            <div key={m.nombre} style={{ display: "grid", gridTemplateColumns: "1fr 280px", gap: 10, alignItems: "center", padding: "4px 2px", borderBottom: "1px solid #D9E1EA" }}>
              <span style={{ fontSize: 12 }}>{m.nombre}</span>
              <select disabled={!canEdit} value={m.categoria} onChange={(e) => save("/v1/prefacturacion/catalogos/mapeo", { nombre: m.nombre, categoria: e.target.value }, "Clasificación guardada")} style={inp}>
                {catNames.map((n) => <option key={n} value={n}>{n}</option>)}<option value="N/A">N/A (no se factura)</option>
              </select>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

export default function Prefacturacion() {
  const { user } = useAuth() || {};
  const role = String(user?.role || "").toLowerCase();
  const [periodos, setPeriodos] = useState([]);
  const [codigo, setCodigo] = useState("");
  const [res, setRes] = useState(null);
  const [cat, setCat] = useState(null);
  const [vista, setVista] = useState("resumen");
  const [contr, setContr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dl, setDl] = useState("");
  const [desde, setDesde] = useState("");   // v101.206.0 · rango de fechas dentro del período
  const [hasta, setHasta] = useState("");
  const per = useMemo(() => periodos.find((p) => p.codigo === codigo), [periodos, codigo]);
  const rango = useMemo(() => ({ ...(desde ? { desde } : {}), ...(hasta ? { hasta } : {}) }), [desde, hasta]);

  useEffect(() => {
    api.get("/v1/prefacturacion/periodos").then(({ data }) => { setPeriodos(data.items || []); setCodigo(data.sugerido); })
      .catch((e) => toast.error(errMsg(e, "No se pudieron cargar los períodos")));
  }, []);
  const loadCat = useCallback(() => api.get("/v1/prefacturacion/catalogos").then(({ data }) => setCat(data)).catch(() => {}), []);
  useEffect(() => { loadCat(); }, [loadCat]);
  const load = useCallback(async () => {
    if (!per) return;
    setLoading(true);
    try {
      const { data } = await api.get("/v1/prefacturacion/resumen", { params: { anio: per.anio, numero: per.numero, ...rango }, timeout: 300000 });
      setRes(data);
    } catch (e) { toast.error(errMsg(e, "No se pudo calcular el período")); } finally { setLoading(false); }
  }, [per, rango]);
  useEffect(() => { load(); }, [load]);

  const download = async (kind) => {
    if (!per) return;
    setDl(kind);
    try {
      const r = await api.get(`/v1/prefacturacion/${kind}`, { params: { anio: per.anio, numero: per.numero, ...rango }, responseType: "blob", timeout: 600000 });
      const u = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = u; a.download = kind === "pdf" ? `Prefacturas_P${per.numero}_${per.anio}_consolidado.pdf` : `Prefacturacion_P${per.numero}_${per.anio}_consolidado.xlsx`;
      a.click(); URL.revokeObjectURL(u);
    } catch { toast.error("No se pudo generar el archivo"); } finally { setDl(""); }
  };

  const t = res?.totales;
  const lista = res?.contratistas || [];
  const barras = [...lista].sort((a, b) => b.totales.total - a.totales.total)
    .map((x) => ({ label: x.contratista, value: Math.max(0, x.totales.total), text: `${money(x.totales.total)} · ${x.kpi.actividades}`,
      tip: `${x.contratista}\nActividades ${money(x.totales.actividades)} · Correcciones ${money(x.totales.correcciones)} · Cambios ${money(x.totales.cambios, true)}` }));
  const abrir = (nombre) => { setContr(nombre); setVista("contratista"); };
  // v101.206.0 · Filtro de contratista: elegir uno abre su detalle; «Todos» vuelve al resumen (sin perder fechas)
  const elegirContr = (v) => { if (v) abrir(v); else { setContr(null); setVista("resumen"); } };
  const nombresContr = [...new Set([...(lista.map((x) => x.contratista)), ...(contr ? [contr] : [])])].sort();

  return (
    <Layout>
      <style>{`.pf-bar:hover span span:first-child{filter:brightness(1.15)}`}</style>
      <div style={{ background: C.bg, fontFamily: "Inter, system-ui, sans-serif", color: C.text, padding: "24px clamp(16px, 3vw, 32px) 48px", minHeight: "100%" }} data-testid="prefacturacion">
        <div style={{ maxWidth: 1376, margin: "0 auto", display: "flex", flexDirection: "column", gap: 20 }}>
          <header style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between", gap: 16 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontFamily: MONO, fontSize: 11, letterSpacing: ".14em", color: C.sub }}>CONTROL DE CALIDAD · CONTRATISTAS</span>
              <h1 style={{ margin: 0, fontSize: 30, fontWeight: 800, letterSpacing: "-.02em", color: C.ink }}>Prefacturación</h1>
              <p style={{ margin: 0, fontSize: 13, color: C.sub }}>Actividades realizadas − correcciones documentales ± cambios de tipo de solución, por corte quincenal.</p>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
              <span style={{ position: "relative" }}>
                <select aria-label="Período" value={codigo} onChange={(e) => { setCodigo(e.target.value); setDesde(""); setHasta(""); }} style={{ ...inp, width: 330, appearance: "none", fontWeight: 600, paddingRight: 30 }}>
                  {periodos.map((p) => <option key={p.codigo} value={p.codigo}>{p.etiqueta}</option>)}
                </select>
                <ChevronDown size={14} color={C.mute} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
              </span>
              <Btn onClick={() => download("pdf")} disabled={!!dl || !lista.length}>{dl === "pdf" ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />} PDF consolidado</Btn>
              <Btn primary onClick={() => download("excel")} disabled={!!dl || !lista.length}>{dl === "excel" ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} color={C.lime} />} Excel consolidado</Btn>
            </div>
          </header>

          <nav style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: 6, borderRadius: 18, background: C.bg, boxShadow: IN }}>
            <Pill active={vista === "resumen"} onClick={() => setVista("resumen")}>Resumen del período</Pill>
            <Pill active={vista === "contratista"} onClick={() => setVista("contratista")}>Contratista</Pill>
            <Pill active={vista === "catalogos"} onClick={() => setVista("catalogos")}>Tarifas y clasificación{res?.sin_clasificar?.length ? ` · ${res.sin_clasificar.length} pendientes` : ""}</Pill>
          </nav>

          {/* v101.206.0 · Filtros: contratista y rango de fechas dentro del período */}
          {vista !== "catalogos" && (
            <section style={{ ...card, flexDirection: "row", flexWrap: "wrap", alignItems: "flex-end", gap: 12, padding: 16 }} data-testid="pf-filtros">
              <label style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 280, flex: "1 1 280px" }}>
                <span style={{ ...cap, fontWeight: 700 }}>Contratista</span>
                <select value={vista === "contratista" && contr ? contr : ""} onChange={(e) => elegirContr(e.target.value)} style={{ ...inp, fontWeight: 600 }} data-testid="pf-filtro-contratista">
                  <option value="">Todos los contratistas</option>
                  {nombresContr.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: 4, width: 170 }}>
                <span style={{ ...cap, fontWeight: 700 }}>Desde</span>
                <input type="date" value={desde} min={per?.desde} max={hasta || per?.hasta} onChange={(e) => setDesde(e.target.value)} style={inp} data-testid="pf-filtro-desde" />
              </label>
              <label style={{ display: "flex", flexDirection: "column", gap: 4, width: 170 }}>
                <span style={{ ...cap, fontWeight: 700 }}>Hasta</span>
                <input type="date" value={hasta} min={desde || per?.desde} max={per?.hasta} onChange={(e) => setHasta(e.target.value)} style={inp} data-testid="pf-filtro-hasta" />
              </label>
              {(desde || hasta || (vista === "contratista" && contr)) && (
                <Btn onClick={() => { setDesde(""); setHasta(""); elegirContr(""); }}><X size={14} /> Quitar filtros</Btn>
              )}
              {(desde || hasta) && <span style={{ ...cap, flexBasis: "100%" }}>Rango parcial del período ({per?.etiqueta}): totales solo de consulta; para revisar o aprobar quite las fechas.</span>}
            </section>
          )}

          {loading && <div style={{ ...cap, display: "flex", alignItems: "center", gap: 8 }}><Loader2 size={14} className="animate-spin" /> Calculando el período…</div>}

          {vista === "resumen" && res && (
            <>
              <Tiles items={[[t.n_actividades, "Actividades facturables"], [money(t.actividades), "Valor de actividades"],
                [money(t.correcciones + t.cambios, true), `Ajustes (${t.n_observadas} correcciones · ${t.n_cambios} cambios)`],
                [money(t.total), "Total a facturar", true], [pct(t.pct_sin_obs), "Informes sin observaciones"]]} />
              {res.sin_clasificar.length > 0 && (
                <button onClick={() => setVista("catalogos")} style={{ ...card, flexDirection: "row", alignItems: "center", gap: 10, color: C.warn, border: 0, cursor: "pointer", textAlign: "left", font: "600 13px Inter" }}>
                  <AlertTriangle size={18} /> {res.sin_clasificar.length} tipos de solución sin categoría de pago ({res.sin_clasificar.reduce((a, s) => a + s.ots, 0)} OT). Clasifíquelos antes de aprobar.
                </button>
              )}
              {lista.length === 0 && <section style={card}><h2 style={h2}>No hay OTs cerradas de contratistas en este período.</h2></section>}
              {lista.length > 0 && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(460px, 1fr))", gap: 18 }}>
                  <section style={card}>
                    <h2 style={h2}>Total a facturar por contratista</h2><p style={cap}>Valor final y número de actividades facturables. Pulse una barra para ver el detalle.</p>
                    <Bars rows={barras} onPick={abrir} />
                  </section>
                  <section style={card}>
                    <h2 style={h2}>Estado de las prefacturas</h2>
                    <p style={cap}>{lista.filter((x) => x.estado === "aprobada").length} aprobadas · {lista.filter((x) => x.estado === "revisada").length} revisadas · {lista.filter((x) => (x.estado || "borrador") === "borrador").length} en borrador</p>
                    <div style={{ display: "flex", height: 16, borderRadius: 999, overflow: "hidden", background: C.bg, boxShadow: IN_SM, gap: 2 }}>
                      {["aprobada", "revisada", "borrador"].map((e) => {
                        const n = lista.filter((x) => (x.estado || "borrador") === e).length;
                        return n ? <div key={e} title={`${ESTADO[e].label}: ${n}`} style={{ flex: n, background: e === "aprobada" ? C.ok : e === "revisada" ? C.serie : "#CBD5E1" }} /> : null;
                      })}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 6 }}>
                      {(res.categorias || []).length > 0 && <div style={{ fontFamily: MONO, fontSize: 10.5, letterSpacing: ".1em", color: C.sub }}>TARIFAS VIGENTES</div>}
                      {(res.categorias || []).map(([n, v]) => (
                        <div key={n} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "4px 0", borderBottom: "1px solid #D9E1EA" }}><span>{n}</span><b>{money(v)}</b></div>
                      ))}
                    </div>
                  </section>
                </div>
              )}
              {lista.length > 0 && (
                <section style={card}>
                  <h2 style={h2}>Prefacturas del período</h2>
                  <div style={{ overflowX: "auto", borderRadius: 14 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 980 }}>
                      <thead><tr>{["Contratista", "Sucursal", "CC", "Act.", "Actividades", "Correcciones", "Cambios", "Total a facturar", "Sin obs.", "Estado"].map((h, i) => <th key={h} style={{ ...th, textAlign: i >= 3 && i <= 8 ? "right" : "left" }}>{h}</th>)}</tr></thead>
                      <tbody>
                        {lista.map((x) => (
                          <tr key={x.contratista} onClick={() => abrir(x.contratista)} style={{ cursor: "pointer" }}>
                            <td style={{ ...td, fontWeight: 600 }}>{x.contratista}</td>
                            <td style={td}>{x.sucursal}</td><td style={td}>{x.centro_costo}</td>
                            <td style={{ ...td, textAlign: "right" }}>{x.kpi.actividades}</td>
                            <td style={{ ...td, textAlign: "right" }}>{money(x.totales.actividades)}</td>
                            <td style={{ ...td, textAlign: "right", color: x.totales.correcciones < 0 ? C.desc : C.text }}>{money(x.totales.correcciones)}</td>
                            <td style={{ ...td, textAlign: "right", color: x.totales.cambios < 0 ? C.desc : C.text }}>{money(x.totales.cambios, true)}</td>
                            <td style={{ ...td, textAlign: "right", fontWeight: 800, color: C.ink }}>{money(x.totales.total)}</td>
                            <td style={{ ...td, textAlign: "right" }}>{pct(x.kpi.pct_sin_obs)}</td>
                            <td style={td}><Estado e={x.estado} /></td>
                          </tr>
                        ))}
                        <tr style={{ background: C.ink }}>
                          {["TOTAL", "", "", t.n_actividades, money(t.actividades), money(t.correcciones), money(t.cambios, true), money(t.total), pct(t.pct_sin_obs), ""].map((v, i) => (
                            <td key={i} style={{ ...td, color: C.lime, fontWeight: 800, textAlign: i >= 3 && i <= 8 ? "right" : "left", borderBottom: 0 }}>{v}</td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p style={cap}>{res.no_facturables} OT cerradas de contratistas con tipo de solución no facturable (N/A) no entran en el cálculo.</p>
                </section>
              )}
              {res && (res.sin_contratista?.total || 0) > 0 && (
                <section style={card}>
                  <h2 style={{ ...h2, display: "flex", alignItems: "center", gap: 8 }}><AlertTriangle size={16} color={C.warn} /> {res.sin_contratista.total} OT cerradas sin contratista (no entran en la prefactura)</h2>
                  <p style={cap}>Las cerró personal interno o un técnico sin contratista. Si alguno trabaja para un contratista, asígnelo en «Tarifas y clasificación › Técnicos y contratista» y la prefactura se recalcula.</p>
                  <div style={{ overflowX: "auto", maxHeight: 320 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead><tr>{["Técnico", "Rol", "Sucursal", "OT cerradas"].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                      <tbody>{res.sin_contratista.por_tecnico.map((x) => (
                        <tr key={x.uid + x.tecnico}><td style={td}>{x.tecnico}</td><td style={td}>{ROL[x.rol] || x.rol || "—"}</td><td style={td}>{x.sucursal || "—"}</td><td style={{ ...td, textAlign: "right", fontFamily: MONO }}>{x.ots}</td></tr>
                      ))}</tbody>
                    </table>
                  </div>
                </section>
              )}
            </>
          )}

          {vista === "contratista" && per && (contr
            ? <Contratista key={`${codigo}-${contr}-${desde}-${hasta}`} per={per} nombre={contr} cat={cat} role={role} rango={rango} onChanged={load} />
            : <section style={card}>
                <h2 style={h2}>Elija un contratista</h2>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>{lista.map((x) => <Btn key={x.contratista} onClick={() => abrir(x.contratista)}>{x.contratista}</Btn>)}</div>
              </section>)}

          {vista === "catalogos" && <Catalogos cat={cat} sinClasificar={res?.sin_clasificar || []} role={role} onChanged={() => { loadCat(); load(); }} />}
        </div>
      </div>
    </Layout>
  );
}
